import { Box, Menu, MenuItem, Typography, styled, useTheme } from '@mui/material';
import { ReactComponent as SearchIcon } from '../assets/icons/24/search.svg';
import { ReactComponent as ArrowIcon } from '../assets/icons/24/arrow_large_white.svg';
import SvgIcon from './SvgIcon';
import { getFilterButtonBorder, getThemeColors } from '../helpers/themeStyles';
import { THEME_MODES } from '../theme';

export const GridHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px 24px 0px',
});

export const FilterToolbar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 24,
  padding: '16px 24px',
  justifyContent: 'space-between',
});

export const SearchInputWrapper = styled(Box)({
  flex: 1,
  maxWidth: 400,
});

export const FilterButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$open',
})(({ theme, $open }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 20px',
  borderRadius: 40,
  border: getFilterButtonBorder(theme, $open),
  cursor: 'pointer',
  minWidth: 140,
  width: 240,
  height: 44,
  boxSizing: 'border-box',
  justifyContent: 'space-between',
  color: getThemeColors(theme).textPrimary,
}));

export const FilterButtonCaption = styled(Typography)(({ theme }) => ({
  fontSize: 10,
  fontWeight: 500,
  color: getThemeColors(theme).textCaption,
  lineHeight: 1,
}));

export const FilterButtonLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  color: getThemeColors(theme).textPrimary,
}));

export const FilterArrow = ({ open }) => {
  const theme = useTheme();
  const colors = getThemeColors(theme);

  return (
    <Box
      component={ArrowIcon}
      sx={{
        flexShrink: 0,
        transform: open ? 'none' : 'rotate(180deg)',
        '& path': { fill: colors.iconOnSurface },
      }}
    />
  );
};

export const ThemedSearchIcon = () => {
  const theme = useTheme();
  const colors = getThemeColors(theme);

  return <SvgIcon icon={SearchIcon} color={colors.textSecondary} />;
};

export const FilterMenu = styled(Menu)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    '& .MuiPaper-root': {
      backgroundColor: colors.menuBg,
      borderRadius: 14,
      marginTop: 4,
      border: `1px solid ${colors.borderSubtle}`,
      minWidth: 240,
      color: colors.menuItemText,
    },
    '& .MuiList-root': {
      padding: 0,
    },
  };
});

export const FilterMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '12px 16px',
  color: getThemeColors(theme).menuItemText,
}));

export const SortMenuSection = styled(Box)(({ theme }) => ({
  padding: '8px 0',
  '&:not(:last-child)': {
    borderBottom: `1px solid ${getThemeColors(theme).borderSubtle}`,
  },
}));

export const SortMenuSectionHeader = styled(Typography)(({ theme }) => ({
  padding: '8px 16px',
  fontSize: 12,
  fontWeight: 500,
  color: getThemeColors(theme).textSecondary,
  textTransform: 'uppercase',
}));

export const SortMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== '$selected',
})(({ theme, $selected }) => {
  const colors = getThemeColors(theme);

  return {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    color: $selected ? colors.filterSelectedText : colors.menuItemText,
    fontWeight: $selected ? 600 : 500,
  };
});

export const StatusMenu = styled(Menu)(({ theme }) => {
  const colors = getThemeColors(theme);

  return {
    '& .MuiPaper-root': {
      backgroundColor: colors.menuBg,
      borderRadius: 14,
      marginTop: 4,
      border: `1px solid ${colors.borderSubtle}`,
    },
    '& .MuiList-root': {
      display: 'flex',
      flexDirection: 'column',
      padding: 16,
      gap: 12,
    },
  };
});

export const StatusMenuItem = styled(MenuItem)({
  padding: 0,
  '&:hover': {
    backgroundColor: 'transparent',
  },
});

export const TabSwitchWrapper = styled(Box)(({ theme }) => {
  const { tabSwitch } = getThemeColors(theme);
  const isLight = theme.palette.mode === THEME_MODES.LIGHT;

  return {
    width: 'fit-content',
    padding: 1,
    borderRadius: 20,
    background: tabSwitch.wrapperBg,
    boxSizing: 'border-box',
    boxShadow: tabSwitch.wrapperBoxShadow,
    border: isLight ? 'none' : tabSwitch.wrapperBorder,
    overflow: 'hidden',
    position: 'relative',
    ...(isLight && {
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 20,
        border: tabSwitch.wrapperBorder,
        pointerEvents: 'none',
        zIndex: 1,
      },
    }),
  };
});

export const TabSwitchContainer = styled(Box)(({ theme }) => {
  const { tabSwitch } = getThemeColors(theme);

  return {
    display: 'flex',
    borderRadius: 20,
    padding: 4,
    background: tabSwitch.containerBg,
  };
});

export const TabSwitchItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$active',
})(({ theme, $active }) => {
  const { tabSwitch } = getThemeColors(theme);
  const activeStyles = $active
    ? {
        color: tabSwitch.tabActiveColor,
        backgroundColor: tabSwitch.tabActiveBg,
        border: `1px solid ${tabSwitch.tabActiveBorder}`,
        boxShadow:
          theme.palette.mode === THEME_MODES.DARK
            ? 'inset -1px -1px 0 rgba(255,255,255,0.15), 1px 1px 2px rgba(0,0,0,0.15)'
            : 'none',
      }
    : {
        color: tabSwitch.tabInactiveColor,
        backgroundColor: 'transparent',
        border: 'none',
        boxShadow: 'none',
      };

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 16px',
    borderRadius: 20,
    height: 32,
    boxSizing: 'border-box',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: $active ? 900 : 500,
    letterSpacing: 0.06,
    textTransform: 'uppercase',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    ...activeStyles,
    '&:hover': {
      color: $active ? tabSwitch.tabActiveColor : tabSwitch.tabInactiveColor,
    },
  };
});
